import { useCallback, useEffect, useRef, useState } from 'react';
import { Bug, ChevronDown, ChevronRight, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { deleteIssue, fetchIssueScreenshot, listIssues, patchIssue } from '../../services/api.js';
import { categoryLabel, ISSUE_STATUSES, statusLabel } from '../IssueReport/issueContext.js';
import styles from './IssuesPage.module.css';

const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * Admin triage queue for issue reports (App renders it only for admins and
 * every /api/issues admin route requires admin). Status chips filter the
 * list; a row expands to the description, the browser-gathered context, the
 * screenshot, and the status / notes editor.
 */
export default function IssuesPage({ initialIssueId = null, onLoadSession, onIssueCountsChange }) {
  const [filter, setFilter] = useState('open');       // status key or 'all'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(initialIssueId);
  const deepLinkPending = useRef(!!initialIssueId);

  const load = useCallback(async ({ append = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const page = await listIssues({
        status: filter === 'all' ? null : filter,
        limit: PAGE_SIZE,
        offset: append ? items.length : 0,
      });
      setItems(prev => (append ? [...prev, ...page.items] : page.items));
      setTotal(page.total);
      setCounts(page.counts);
      onIssueCountsChange?.(page.counts);
    } catch (err) {
      setError(err?.message || 'Could not load issues');
    } finally {
      setLoading(false);
    }
  }, [filter, items.length, onIssueCountsChange]);

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // A deep link (?issue=<id>, from the admin email) may point at an issue that
  // isn't "open" any more — widen the filter once so it can be found.
  useEffect(() => {
    if (!deepLinkPending.current || loading) return;
    if (items.some(i => i.id === initialIssueId)) { deepLinkPending.current = false; return; }
    if (filter !== 'all') setFilter('all'); else deepLinkPending.current = false;
  }, [items, loading, filter, initialIssueId]);

  const applyPatch = useCallback(async (id, body) => {
    const updated = await patchIssue(id, body);
    setItems(prev => prev.map(i => (i.id === id ? updated : i)));
    // Status moves change the chip counts; refetch them cheaply.
    if (body.status) {
      const page = await listIssues({ limit: 1 });
      setCounts(page.counts);
      onIssueCountsChange?.(page.counts);
    }
    return updated;
  }, [onIssueCountsChange]);

  const remove = useCallback(async (id) => {
    if (!window.confirm('Delete this report and its screenshot? This cannot be undone.')) return;
    await deleteIssue(id);
    setItems(prev => prev.filter(i => i.id !== id));
    setTotal(t => Math.max(0, t - 1));
    const page = await listIssues({ limit: 1 });
    setCounts(page.counts);
    onIssueCountsChange?.(page.counts);
  }, [onIssueCountsChange]);

  const chips = [{ key: 'all', label: 'All' }, ...ISSUE_STATUSES];
  const countFor = key => (key === 'all'
    ? Object.values(counts).reduce((a, b) => a + b, 0)
    : counts[key] ?? 0);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}><Bug size={18} /> Issue Reports</h1>
        <span className={styles.adminTag}>Admin</span>
        <span className={styles.spacer} />
        <button type="button" className={styles.iconBtn} onClick={() => load()} disabled={loading} title="Refresh">
          <RefreshCw size={15} className={loading ? styles.spin : undefined} />
        </button>
      </div>

      <div className={styles.chips} role="tablist" aria-label="Filter by status">
        {chips.map(c => (
          <button
            key={c.key}
            type="button"
            role="tab"
            aria-selected={filter === c.key}
            className={filter === c.key ? styles.chipOn : styles.chip}
            onClick={() => setFilter(c.key)}
          >
            {c.label} <b>{countFor(c.key)}</b>
          </button>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!loading && items.length === 0 && !error && (
        <div className={styles.empty}>No {filter === 'all' ? '' : statusLabel(filter).toLowerCase() + ' '}reports.</div>
      )}

      {items.length > 0 && (
        <div className={styles.list}>
          <div className={`${styles.row} ${styles.rowHead}`}>
            <span />
            <span>Filed</span>
            <span>Reporter</span>
            <span>Type</span>
            <span>Title</span>
            <span>Page</span>
            <span>Status</span>
          </div>
          {items.map(issue => (
            <IssueRow
              key={issue.id}
              issue={issue}
              open={expanded === issue.id}
              onToggle={() => setExpanded(prev => (prev === issue.id ? null : issue.id))}
              onPatch={body => applyPatch(issue.id, body)}
              onDelete={() => remove(issue.id)}
              onLoadSession={onLoadSession}
            />
          ))}
        </div>
      )}

      {items.length < total && (
        <div className={styles.more}>
          <button type="button" className={styles.chip} onClick={() => load({ append: true })} disabled={loading}>
            {loading ? 'Loading…' : `Load more (${total - items.length} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
}

function IssueRow({ issue, open, onToggle, onPatch, onDelete, onLoadSession }) {
  const [notes, setNotes] = useState(issue.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [shotUrl, setShotUrl] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => { setNotes(issue.admin_notes || ''); }, [issue.admin_notes]);

  // Fetch the (auth-gated) screenshot only when the row is opened.
  useEffect(() => {
    if (!open || !issue.has_screenshot) return undefined;
    let url = null;
    let cancelled = false;
    fetchIssueScreenshot(issue.id)
      .then(u => { url = u; if (!cancelled) setShotUrl(u); else URL.revokeObjectURL(u); })
      .catch(e => { if (!cancelled) setErr(e?.message || 'Could not load the screenshot'); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); setShotUrl(null); };
  }, [open, issue.id, issue.has_screenshot]);

  useEffect(() => {
    if (open) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = e => { if (e.key === 'Escape') setLightbox(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const run = async (fn) => {
    setSaving(true);
    setErr('');
    try { await fn(); } catch (e) { setErr(e?.message || 'Update failed'); } finally { setSaving(false); }
  };

  const saveNotes = () => {
    if (notes === (issue.admin_notes || '')) return;
    run(() => onPatch({ admin_notes: notes }));
  };

  const ctx = issue.context || {};
  const reporter = issue.reporter_name
    ? <><strong>{issue.reporter_name}</strong><small>{issue.reporter_email}</small></>
    : <strong>{issue.reporter_email}</strong>;

  return (
    <div ref={rowRef} className={open ? styles.itemOpen : styles.item}>
      <button type="button" className={styles.row} onClick={onToggle} aria-expanded={open}>
        <span className={styles.caret}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        <span className={styles.when}>{fmtDate(issue.created_at)}</span>
        <span className={styles.reporter}>{reporter}</span>
        <span><span className={`${styles.cat} ${styles[`cat_${issue.category}`] || ''}`}>{categoryLabel(issue.category, { short: true })}</span></span>
        <span className={styles.rowTitle}>{issue.title}</span>
        <span className={styles.muted}>{issue.page || '—'}</span>
        <span><span className={`${styles.status} ${styles[`status_${issue.status}`] || ''}`}>{statusLabel(issue.status)}</span></span>
      </button>

      {open && (
        <div className={styles.detail}>
          <div className={styles.detailMain}>
            <pre className={styles.body}>{issue.body}</pre>

            {shotUrl && (
              <figure className={styles.shot}>
                <button type="button" className={styles.shotBtn} onClick={() => setLightbox(true)} title="View full size">
                  <img src={shotUrl} alt="Screenshot attached to the report" />
                </button>
                <figcaption>Screenshot — click to enlarge</figcaption>
              </figure>
            )}

            <details className={styles.context}>
              <summary>Context</summary>
              <pre>{JSON.stringify(ctx, null, 2)}</pre>
            </details>
          </div>

          <aside className={styles.side}>
            <dl className={styles.facts}>
              <dt>Reporter</dt><dd>{issue.reporter_email}</dd>
              <dt>Filed</dt><dd>{fmtDate(issue.created_at)}</dd>
              {issue.resolved_at && <><dt>Resolved</dt><dd>{fmtDate(issue.resolved_at)}</dd></>}
              {issue.url && (
                <><dt>URL</dt><dd><a href={issue.url} target="_blank" rel="noreferrer" className={styles.link}>{issue.url.replace(/^https?:\/\/[^/]+/, '') || '/'}<ExternalLink size={11} /></a></dd></>
              )}
              {issue.session_id && (
                <><dt>Session</dt><dd>
                  <button type="button" className={styles.linkBtn} onClick={() => onLoadSession?.(issue.session_id)} title={issue.session_id}>
                    {ctx.trackName || issue.session_id.slice(0, 8)}{ctx.sessionLabel ? ` — ${ctx.sessionLabel}` : ''}
                  </button>
                </dd></>
              )}
              {ctx.masquerade_actor && <><dt>Filed by admin</dt><dd>{ctx.masquerade_actor.email}</dd></>}
              {ctx.userAgent && <><dt>Browser</dt><dd className={styles.ua}>{ctx.userAgent}</dd></>}
              {ctx.viewport && <><dt>Viewport</dt><dd>{ctx.viewport.w}×{ctx.viewport.h} @{ctx.viewport.dpr}x</dd></>}
              {Array.isArray(ctx.recentErrors) && ctx.recentErrors.length > 0 && (
                <><dt>Recent errors</dt><dd>{ctx.recentErrors.length} logged (see context)</dd></>
              )}
            </dl>

            <label className={styles.ctl}>
              <span>Status</span>
              <select value={issue.status} disabled={saving} onChange={e => run(() => onPatch({ status: e.target.value }))}>
                {ISSUE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className={styles.ctl}>
              <span>Admin notes</span>
              <textarea rows={4} value={notes} disabled={saving} onChange={e => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Internal notes (not shown to the reporter)" />
            </label>
            {err && <div className={styles.error}>{err}</div>}
            <div className={styles.sideActions}>
              <a className={styles.mailto} href={`mailto:${issue.reporter_email}?subject=${encodeURIComponent(`Re: ${issue.title}`)}`}>Email reporter</a>
              <button type="button" className={styles.danger} onClick={onDelete} disabled={saving}><Trash2 size={13} /> Delete</button>
            </div>
          </aside>
        </div>
      )}

      {lightbox && shotUrl && (
        <div className={styles.lightbox} role="presentation" onClick={() => setLightbox(false)}>
          <img src={shotUrl} alt="Screenshot, full size" />
        </div>
      )}
    </div>
  );
}
